/**
 * Scan Samples: move scanned samples into a location, either as each one is scanned (with Undo) or
 * collected and moved together. The location can be picked or scanned, and remembered on the device.
 *
 * The provider holds the state and does the writes; the `SampleScanning` view draws it into whatever
 * container the host gives it. Scans come from a `HeadlightQR` provider (`listen`, or `handleResolution` for one
 * resolution) or plain `handleScan` calls.
 */

const libPictProvider = require('pict-provider');
const libSampleScanStatus = require('../Sample-Scan-Status.js');
const libSampleMove = require('../Sample-Move.js');
const AppSampleScanStore = require('../Sample-Scan-Store.js');

const STORAGE_PREFIX = 'HeadlightScan-';
// Check Samples into Lab: the status every collected sample takes, and the log entry each one gets.
const RECEIVED_STATUS = 'RECV';
const RECEIVED_NOTES = 'Sample received at lab.';

/**
 * @typedef {Object} SampleScanHost
 * @property {(pToast: { message: string, type: 'is-info'|'is-success'|'is-warning'|'is-danger' }) => void} [notify]
 * @property {(pSample: Object) => any} [printLabel] - Shows a print button on collected samples.
 * @property {{ toggle: (pContainerID: string) => Promise<boolean>, isOpen: () => boolean }} [camera] - Shows the camera button.
 * @property {(pMove: { Location: Object, Moved: Array<Object>, Failed: Array<Object>, Via: 'move-all'|'scan' }) => void} [moved] -
 *     Called after samples are moved (Move all, or a scan saved right away), with the samples moved and any that could not be.
 * @property {(pMark: { Received: Array<Object>, Failed: Array<Object> }) => void} [received] - Called after Mark Samples
 *     Received, with the samples set to RECV and any that could not be.
 */

/**
 * @typedef {Object} SampleScan
 * @property {'Sample'|'Location'|'Action'|'Deleted'|'NotFound'} Kind
 * @property {string} [Entity] - For Deleted.
 * @property {Object} [Record] - The sample or location.
 * @property {'collect'|'save-each'|'submit'|'clear'} [Action]
 */

/**
 * @typedef {Object} ReceivedRow
 * @property {Object} Sample
 * @property {'working'|'ok'|'warn'|'error'|'undone'} State
 * @property {string} Message
 * @property {number} [IDLocation]
 * @property {Array<number>} [PreviousIDLocations]
 * @property {string} [PreviousStatus]
 * @property {string} [NewStatus]
 */

const _DefaultOptions =
{
	ProviderIdentifier: 'SampleScanning',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
	// A move replaces the sample's current location; false keeps adding locations instead.
	RemoveFromCurrentLocation: true,
	// The view this provider draws with, and the prefix of the element IDs it uses. A second cart (attachInstance)
	// uses its own name for both, so it can be open beside the panels' Scan Samples.
	ViewHash: 'SampleScanning',
	IDPrefix: 'SampleScanning',
	// How this cart names itself to HeadlightQR while it listens.
	ScanHandlerHash: 'sample-scanning',
};

class SampleScanningProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DefaultOptions, pOptions), pServiceHash);
		/** @type {any} */
		this.pict;
		/** @type {import('../Sample-Scan-Store.js').SampleScanStore} */
		this.store = (pOptions && pOptions.Store) || new AppSampleScanStore(this.pict);
		/** @type {{ getItem: (pKey: string) => string|null, setItem: (pKey: string, pValue: string) => void, removeItem: (pKey: string) => void }|null} */
		this.storage = (pOptions && pOptions.Storage) || null;
		/** @type {SampleScanHost} */
		this.host = {};
		/** Samples collected while "Save each scan right away" is off. */
		this.samples = [];
		/** @type {Array<number>} collected samples the user can't take out of the list (keepSample) */
		this.keptSampleIDs = [];
		/** @type {Object|null} the only location this session moves samples into (fixLocation) */
		this.fixedLocation = null;
		/** Check Samples into Lab is running. */
		this.markingReceived = false;
		/** @type {ReceivedRow[]} scans saved straight away, newest first */
		this.received = [];
		this.location = null;
		this.saveEachScan = false;
		this.rememberLocation = false;
		/** @type {Array<Object>|null} */
		this.locations = null;
		this._locationsLoading = null;
		/** @type {import('../Sample-Scan-Status.js').ReceiveConfig|null} */
		this.receiveConfig = null;
		this._receiveConfigLoading = null;
	}

	/**
	 * Wire the host's messages, label printing and camera. Call before open().
	 * @param {SampleScanHost} pHost
	 */
	connect(pHost)
	{
		this.host = pHost || {};
	}

	// ---- Lifecycle ------------------------------------------------------------

	/**
	 * Start a fresh scanning session.
	 * @param {Object} [pOptions]
	 * @param {boolean} [pOptions.SaveEachScan] - Left out, the device's last setting is kept.
	 * @param {string} [pOptions.DestinationAddress] - Where the view draws; defaults to the view's own.
	 */
	open(pOptions)
	{
		const tmpOptions = pOptions || {};
		this.samples = [];
		this.keptSampleIDs = [];
		this.fixedLocation = null;
		this.received = [];
		this.restoreDeviceSettings();
		if (typeof tmpOptions.SaveEachScan === 'boolean')
		{
			this.setSaveEachScan(tmpOptions.SaveEachScan, true);
		}
		this.loadLocations().then(() => this.refresh());
		this.loadReceiveConfig().then(() => this.refresh());
		const tmpView = this.view;
		if (tmpView)
		{
			tmpView.renderShell(tmpOptions.DestinationAddress);
		}
	}

	close()
	{
		this.stopListening();
		this.samples = [];
		this.keptSampleIDs = [];
		this.received = [];
		if (this.fixedLocation)
		{
			// A fixed location belonged to that session (a page's own location), never to the next one.
			this.fixedLocation = null;
			this.location = null;
		}
		this.forgetLocation();
	}

	/** After a finish, clear or Move all: drop the location, unless it is remembered on the device or fixed. */
	forgetLocation()
	{
		if (!this.rememberLocation && !this.fixedLocation)
		{
			this.location = null;
		}
	}

	/** @return {any} the view this provider draws with */
	get view()
	{
		return this.pict.views[this.options.ViewHash];
	}

	/** Redraw the settings and lists (not the camera). */
	refresh()
	{
		const tmpView = this.view;
		if (tmpView)
		{
			tmpView.renderBody();
		}
	}

	/**
	 * @param {string} pMessage
	 * @param {'is-info'|'is-success'|'is-warning'|'is-danger'} pType
	 */
	notify(pMessage, pType)
	{
		if (typeof this.host.notify === 'function')
		{
			this.host.notify({ message: pMessage, type: pType });
			return;
		}
		this.log.info(`[SampleScanning] ${pMessage}`);
	}

	// ---- Device settings ------------------------------------------------------

	/** @return {string} */
	get storageScope()
	{
		const tmpUser = (this.pict.AppData && this.pict.AppData.CurrentUser) || {};
		return String(tmpUser.CustomerID || tmpUser.IDCustomer || 'default');
	}

	/**
	 * @return {{ getItem: Function, setItem: Function, removeItem: Function }|null}
	 */
	getStorage()
	{
		if (this.storage)
		{
			return this.storage;
		}
		try
		{
			return (typeof window !== 'undefined' && window.localStorage) ? window.localStorage : null;
		}
		catch (pError)
		{
			return null;
		}
	}

	/**
	 * @param {string} pKey
	 * @return {string|null}
	 */
	readSetting(pKey)
	{
		try
		{
			const tmpStorage = this.getStorage();
			return tmpStorage ? tmpStorage.getItem(`${STORAGE_PREFIX}${pKey}`) : null;
		}
		catch (pError)
		{
			return null;
		}
	}

	/**
	 * @param {string} pKey
	 * @param {string|null} pValue - null removes the setting
	 */
	writeSetting(pKey, pValue)
	{
		try
		{
			const tmpStorage = this.getStorage();
			if (!tmpStorage)
			{
				return;
			}
			if (pValue === null)
			{
				tmpStorage.removeItem(`${STORAGE_PREFIX}${pKey}`);
			}
			else
			{
				tmpStorage.setItem(`${STORAGE_PREFIX}${pKey}`, pValue);
			}
		}
		catch (pError)
		{
			// Private windows can refuse storage; the settings then last for this visit only.
		}
	}

	restoreDeviceSettings()
	{
		this.saveEachScan = (this.readSetting('SaveEachScan') === '1');
		const tmpRemembered = this.readSetting(`Location-${this.storageScope}`);
		this.rememberLocation = !!tmpRemembered;
		if (!tmpRemembered || (this.location && String(this.location.IDLocation) === tmpRemembered))
		{
			return;
		}
		const tmpFromList = (this.locations || []).find((pLocation) => String(pLocation.IDLocation) === tmpRemembered);
		if (tmpFromList)
		{
			this.location = tmpFromList;
			return;
		}
		Promise.resolve(this.store.getLocation(Number(tmpRemembered))).then((pLocation) =>
		{
			if (pLocation && pLocation.IDLocation && !this.location)
			{
				this.location = pLocation;
				this.refresh();
			}
		}).catch(() =>
		{
			this.writeSetting(`Location-${this.storageScope}`, null);
		});
	}

	/**
	 * @param {boolean} pOn
	 * @param {boolean} [pSkipRefresh]
	 */
	setSaveEachScan(pOn, pSkipRefresh)
	{
		this.saveEachScan = !!pOn;
		this.writeSetting('SaveEachScan', this.saveEachScan ? '1' : '0');
		if (!pSkipRefresh)
		{
			this.refresh();
		}
	}

	/**
	 * @param {boolean} pOn
	 */
	setRememberLocation(pOn)
	{
		if (this.fixedLocation)
		{
			return;
		}
		this.rememberLocation = !!pOn;
		this.writeSetting(`Location-${this.storageScope}`, (this.rememberLocation && this.location && this.location.IDLocation) ? String(this.location.IDLocation) : null);
		this.refresh();
	}

	// ---- Locations and configuration ------------------------------------------

	/** @return {Promise<Array<Object>>} */
	loadLocations()
	{
		if (this.locations)
		{
			return Promise.resolve(this.locations);
		}
		if (!this._locationsLoading)
		{
			this._locationsLoading = Promise.resolve(this.store.listLocations()).catch(() => []).then((pLocations) =>
			{
				this.locations = Array.isArray(pLocations) ? pLocations : [];
				this._locationsLoading = null;
				return this.locations;
			});
		}
		return this._locationsLoading;
	}

	/** @return {Promise<import('../Sample-Scan-Status.js').ReceiveConfig>} empty when there is none */
	loadReceiveConfig()
	{
		if (this.receiveConfig)
		{
			return Promise.resolve(this.receiveConfig);
		}
		if (!this._receiveConfigLoading)
		{
			this._receiveConfigLoading = Promise.resolve(this.store.readReceiveConfig()).catch(() => null).then((pConfig) =>
			{
				this._receiveConfigLoading = null;
				// Only a successful read is kept, so a network blip doesn't switch status updates off for the session.
				if (pConfig)
				{
					this.receiveConfig = (typeof pConfig === 'object') ? pConfig : {};
				}
				return pConfig || {};
			});
		}
		return this._receiveConfigLoading;
	}

	/**
	 * Use a location for the scans that follow.
	 * @param {Object|null} pLocation
	 */
	setLocation(pLocation)
	{
		if (this.fixedLocation)
		{
			return;
		}
		this.location = pLocation || null;
		if (this.rememberLocation)
		{
			this.writeSetting(`Location-${this.storageScope}`, (this.location && this.location.IDLocation) ? String(this.location.IDLocation) : null);
		}
		this.refresh();
	}

	/**
	 * Move samples only into this location for the rest of the session (e.g. a location's own page adding samples to
	 * it): the picker, a scanned location and the remember switch can't change it, and finishing keeps it.
	 * @param {Object} pLocation
	 */
	fixLocation(pLocation)
	{
		if (!pLocation || !pLocation.IDLocation)
		{
			return;
		}
		this.fixedLocation = null;
		this.setLocation(pLocation);
		this.fixedLocation = pLocation;
	}

	/**
	 * @param {string|number} pIDLocation - from the picker; empty clears the location
	 */
	pickLocation(pIDLocation)
	{
		const tmpLocation = (this.locations || []).find((pLocation) => String(pLocation.IDLocation) === String(pIDLocation));
		this.setLocation(tmpLocation || null);
	}

	/** @return {string} the status samples moved into the current location take, or '' */
	get locationStatus()
	{
		return this.location ? libSampleScanStatus.getConfiguredStatus(this.receiveConfig || {}, this.location) : '';
	}

	// ---- Scans ----------------------------------------------------------------

	/**
	 * @param {SampleScan} pScan
	 * @return {Promise<void>}
	 */
	async handleScan(pScan)
	{
		const tmpScan = pScan || { Kind: 'NotFound' };
		if (tmpScan.Kind === 'Action')
		{
			if (tmpScan.Action === 'save-each' || tmpScan.Action === 'collect')
			{
				this.setSaveEachScan(tmpScan.Action === 'save-each');
			}
			else if (tmpScan.Action === 'submit')
			{
				if (this.samples.length)
				{
					await this.moveAll();
				}
				else
				{
					this.finish();
				}
			}
			else if (tmpScan.Action === 'clear')
			{
				this.clear();
			}
			return;
		}
		if (tmpScan.Kind === 'Location' && tmpScan.Record && tmpScan.Record.IDLocation)
		{
			if (this.fixedLocation)
			{
				if (Number(tmpScan.Record.IDLocation) !== Number(this.fixedLocation.IDLocation))
				{
					this.notify(`Samples here can only go into ${this.fixedLocation.Name || 'this location'}.`, 'is-warning');
				}
				return;
			}
			if (!this.location || this.location.IDLocation !== tmpScan.Record.IDLocation)
			{
				this.setLocation(tmpScan.Record);
			}
			return;
		}
		if (tmpScan.Kind === 'Sample' && tmpScan.Record && tmpScan.Record.IDSample)
		{
			if (this.saveEachScan)
			{
				await this.receiveSample(tmpScan.Record);
			}
			else if (!this.samples.some((pSample) => pSample.IDSample === tmpScan.Record.IDSample))
			{
				this.samples.push(tmpScan.Record);
				this.refresh();
			}
			return;
		}
		if (tmpScan.Kind === 'Deleted')
		{
			this.notify(`That ${String(tmpScan.Entity || 'record').toLowerCase()} was deleted.`, 'is-warning');
			return;
		}
		this.notify('Nothing found for that code.', 'is-warning');
	}

	/**
	 * Take scans from a `HeadlightQR` provider while the panel is open: samples, locations (raw codes too) and the
	 * Scan Samples action cards. The keyboard scanner is held on meanwhile, even in apps that leave it off (forms).
	 * Safe to call again; the previous registration is replaced.
	 * @param {any} pHeadlightQR
	 */
	listen(pHeadlightQR)
	{
		this.stopListening();
		if (!pHeadlightQR || typeof pHeadlightQR.pushScanHandler !== 'function')
		{
			return;
		}
		this._removeScanHandler = pHeadlightQR.pushScanHandler(
			{
				Hash: this.options.ScanHandlerHash,
				Mode: 'Session',
				Label: 'Scan Samples',
				Workflows: [ 'cart', 'receive' ],
				Accept: [ 'Sample', 'Location' ],
				RawEntities: [ 'Sample', 'Location' ],
				onScan: (pResolution) =>
				{
					// Not awaited: a move can take seconds, and the scan's tone shouldn't wait for it.
					this.handleResolution(pResolution).catch((pError) => this.log.error(`Scan Samples could not handle a scan: ${(pError && pError.message) || pError}`));
					return true;
				},
			});
		this._releaseWedge = (typeof pHeadlightQR.holdWedge === 'function') ? pHeadlightQR.holdWedge() : null;
	}

	stopListening()
	{
		if (this._removeScanHandler)
		{
			this._removeScanHandler();
			this._removeScanHandler = null;
		}
		if (this._releaseWedge)
		{
			this._releaseWedge();
			this._releaseWedge = null;
		}
	}

	/**
	 * A `HeadlightQR` resolution.
	 * @param {{ Status: string, Entity?: string, Record?: Object, Decoded?: any }} pResolution
	 * @return {Promise<void>}
	 */
	handleResolution(pResolution)
	{
		return this.handleScan(SampleScanningProvider.fromResolution(pResolution));
	}

	/**
	 * @param {{ Status: string, Entity?: string, Record?: Object, Decoded?: any }} pResolution
	 * @return {SampleScan}
	 */
	static fromResolution(pResolution)
	{
		const tmpResolution = pResolution || { Status: '' };
		if (tmpResolution.Status === 'Found' && (tmpResolution.Entity === 'Sample' || tmpResolution.Entity === 'Location'))
		{
			return { Kind: tmpResolution.Entity, Record: tmpResolution.Record };
		}
		if (tmpResolution.Status === 'Action' && tmpResolution.Decoded)
		{
			const tmpWorkflow = String(tmpResolution.Decoded.Workflow || '').toLowerCase();
			const tmpVerb = String(tmpResolution.Decoded.Verb || '').toLowerCase();
			if (tmpWorkflow === 'receive')
			{
				return { Kind: 'Action', Action: 'save-each' };
			}
			if (tmpWorkflow === 'cart' && tmpVerb === 'submit')
			{
				return { Kind: 'Action', Action: 'submit' };
			}
			if (tmpWorkflow === 'cart' && tmpVerb === 'clear')
			{
				return { Kind: 'Action', Action: 'clear' };
			}
			if (tmpWorkflow === 'cart')
			{
				return { Kind: 'Action', Action: 'collect' };
			}
		}
		if (tmpResolution.Status === 'Deleted')
		{
			return { Kind: 'Deleted', Entity: tmpResolution.Entity, Record: tmpResolution.Record };
		}
		return { Kind: 'NotFound' };
	}

	// ---- Moves ----------------------------------------------------------------

	/**
	 * Move one sample into the location now, with any status the location sets.
	 * @param {Object} pSample
	 * @return {Promise<ReceivedRow|null>}
	 */
	async receiveSample(pSample)
	{
		const tmpLocation = this.location;
		if (!tmpLocation || !tmpLocation.IDLocation)
		{
			const tmpWarning = { Sample: pSample, State: 'warn', Message: 'Not saved: choose or scan a location first.' };
			this.received.unshift(tmpWarning);
			this.refresh();
			return tmpWarning;
		}
		const tmpExisting = this.received.find((pRow) => pRow.Sample.IDSample === pSample.IDSample && pRow.State === 'ok');
		if (tmpExisting && tmpExisting.IDLocation === tmpLocation.IDLocation)
		{
			this.notify(`${pSample.Name || pSample.QRCode} is already in ${tmpLocation.Name}.`, 'is-warning');
			return null;
		}
		/** @type {ReceivedRow} */
		const tmpRow = { Sample: pSample, State: 'working', Message: `Moving into ${tmpLocation.Name}…`, IDLocation: tmpLocation.IDLocation };
		this.received.unshift(tmpRow);
		this.refresh();
		try
		{
			const tmpPosition = await this.store.getPosition();
			const tmpMove = await libSampleMove.moveSampleToLocation(this.store, pSample, tmpLocation,
				{
					RemoveFromCurrentLocation: this.options.RemoveFromCurrentLocation,
					ReceiveConfig: await this.loadReceiveConfig(),
					Position: tmpPosition,
					Locations: await this.loadLocations(),
					Notes: SampleScanningProvider.receivedNotes,
				});
			tmpRow.PreviousIDLocations = tmpMove.PreviousIDLocations;
			tmpRow.PreviousStatus = tmpMove.PreviousStatus;
			tmpRow.NewStatus = tmpMove.StatusDecision.Status || undefined;
			tmpRow.State = 'ok';
			tmpRow.Message = `Moved into ${tmpLocation.Name}${SampleScanningProvider.describeStatusChange(tmpMove.StatusDecision)}.`;
		}
		catch (pError)
		{
			this.log.error(`Could not receive sample ${pSample.IDSample} into location ${tmpLocation.IDLocation}: ${(pError && pError.message) || pError}`);
			tmpRow.State = 'error';
			tmpRow.Message = 'Could not be moved. Scan it again to retry.';
		}
		this.refresh();
		this.reportMoved(tmpLocation, (tmpRow.State === 'ok') ? [ pSample ] : [], (tmpRow.State === 'ok') ? [] : [ pSample ], 'scan');
		return tmpRow;
	}

	/**
	 * The log entry for a scan saved right away.
	 * @param {{ To: string, From: string }} pMove - From is '' when the sample wasn't in a location
	 * @return {string}
	 */
	static receivedNotes(pMove)
	{
		return pMove.From ? `Sample received from ${pMove.From} into ${pMove.To}` : `Sample received into ${pMove.To}`;
	}

	/**
	 * The log entry for Move all (the panels' cart and a sample's Location tab).
	 * @param {{ To: string, From: string }} pMove - From is '' when the sample wasn't in a location
	 * @return {string}
	 */
	static movedNotes(pMove)
	{
		return pMove.From ? `Sample Location updated from ${pMove.From} to ${pMove.To}` : `Sample Location updated to ${pMove.To}`;
	}

	/**
	 * @param {{ Status: string, Kept?: string }} pStatus
	 * @return {string}
	 */
	static describeStatusChange(pStatus)
	{
		if (pStatus.Status)
		{
			return `; status set to ${pStatus.Status}`;
		}
		if (pStatus.Kept)
		{
			return `; status left as ${pStatus.Kept}`;
		}
		return '';
	}

	/**
	 * Put a moved sample back where it was, with its previous status.
	 * @param {number} pIDSample
	 * @return {Promise<void>}
	 */
	async undoReceive(pIDSample)
	{
		const tmpRow = this.received.find((pRow) => pRow.Sample.IDSample === Number(pIDSample) && pRow.State === 'ok');
		if (!tmpRow)
		{
			return;
		}
		tmpRow.State = 'working';
		tmpRow.Message = 'Undoing…';
		this.refresh();
		try
		{
			const tmpPrevious = (tmpRow.PreviousIDLocations || []).slice().reverse();
			for (const tmpIDLocation of tmpPrevious)
			{
				await this.store.addSampleToLocation(tmpRow.Sample.IDSample, tmpIDLocation);
			}
			if (!tmpPrevious.includes(tmpRow.IDLocation))
			{
				await this.store.removeSampleFromLocation(tmpRow.Sample.IDSample, tmpRow.IDLocation);
			}
			if (tmpRow.NewStatus)
			{
				await this.store.setSampleStatus(tmpRow.Sample.IDSample, tmpRow.PreviousStatus);
			}
			await this.store.createSampleLog(
				{
					IDSample: tmpRow.Sample.IDSample,
					IDLocation: tmpPrevious[tmpPrevious.length - 1],
					FromStatus: tmpRow.NewStatus || undefined,
					ToStatus: tmpRow.NewStatus ? tmpRow.PreviousStatus : undefined,
					Notes: 'Receive undone',
				});
			tmpRow.State = 'undone';
			tmpRow.Message = 'Undone: back where it was.';
		}
		catch (pError)
		{
			this.log.error(`Could not undo receiving sample ${tmpRow.Sample.IDSample}: ${(pError && pError.message) || pError}`);
			tmpRow.State = 'error';
			tmpRow.Message = 'Undo failed. Check the sample\'s location before trying again.';
		}
		this.refresh();
	}

	/** Clear the list of saved scans. Each one was already saved, so nothing is written. */
	finish()
	{
		const tmpCount = this.received.filter((pRow) => pRow.State === 'ok').length;
		if (this.received.length > 0)
		{
			this.notify(`Moved ${tmpCount} sample${tmpCount === 1 ? '' : 's'}.`, 'is-success');
		}
		this.received = [];
		this.forgetLocation();
		this.refresh();
	}

	/**
	 * Collect a sample the user can't take out of the list (no remove button, and Clear leaves it), e.g. the sample
	 * a page is about when it opens the cart to move that sample. It still moves with Move all.
	 * @param {Object} pSample
	 */
	keepSample(pSample)
	{
		if (!pSample || !pSample.IDSample)
		{
			return;
		}
		const tmpIDSample = Number(pSample.IDSample);
		if (!this.keptSampleIDs.includes(tmpIDSample))
		{
			this.keptSampleIDs.push(tmpIDSample);
		}
		if (!this.samples.some((pCollected) => Number(pCollected.IDSample) === tmpIDSample))
		{
			this.samples.push(pSample);
		}
		this.refresh();
	}

	/**
	 * @param {number|string} pIDSample
	 * @return {boolean} whether the sample is one the user can't take out of the list
	 */
	isKept(pIDSample)
	{
		return this.keptSampleIDs.includes(Number(pIDSample));
	}

	/**
	 * @param {number} pIDSample - a kept sample stays
	 */
	removeSample(pIDSample)
	{
		if (this.isKept(pIDSample))
		{
			return;
		}
		this.samples = this.samples.filter((pSample) => pSample.IDSample !== Number(pIDSample));
		this.refresh();
	}

	/** Clear the collected samples (all but the kept ones) and the list of saved scans. */
	clear()
	{
		this.forgetLocation();
		this.samples = this.samples.filter((pSample) => this.isKept(pSample.IDSample));
		this.received = [];
		this.refresh();
	}

	/**
	 * Move every collected sample into the location.
	 * @return {Promise<void>}
	 */
	async moveAll()
	{
		if (!this.location || !this.location.IDLocation || !this.samples.length)
		{
			if (this.samples.length)
			{
				this.notify('Choose or scan a location first.', 'is-warning');
			}
			this.refresh();
			return;
		}
		const tmpLocation = this.location;
		this.notify(`Moving ${this.samples.length} sample${this.samples.length === 1 ? '' : 's'} to ${tmpLocation.Name}…`, 'is-info');
		const tmpPosition = (await this.store.getPosition()) || {};
		const tmpReceiveConfig = await this.loadReceiveConfig();
		const tmpLocations = await this.loadLocations();
		const tmpFailed = [];
		let tmpStatusCount = 0;
		for (const tmpSample of this.samples)
		{
			try
			{
				const tmpMove = await libSampleMove.moveSampleToLocation(this.store, tmpSample, tmpLocation,
					{
						RemoveFromCurrentLocation: this.options.RemoveFromCurrentLocation,
						ReceiveConfig: tmpReceiveConfig,
						Position: { CaptureLatitude: tmpPosition.CaptureLatitude || tmpSample.CaptureLatitude, CaptureLongitude: tmpPosition.CaptureLongitude || tmpSample.CaptureLongitude },
						Locations: tmpLocations,
						Notes: SampleScanningProvider.movedNotes,
					});
				if (tmpMove.StatusDecision.Status)
				{
					tmpStatusCount++;
				}
			}
			catch (pError)
			{
				this.log.error(`Could not move sample ${tmpSample.IDSample} to location ${tmpLocation.IDLocation}: ${(pError && pError.message) || pError}`);
				tmpFailed.push(tmpSample);
			}
		}
		const tmpMovedCount = this.samples.length - tmpFailed.length;
		const tmpMoved = this.samples.filter((pSample) => !tmpFailed.includes(pSample));
		// Moved samples leave the list, so a second Move all can't move them again; failures stay to retry.
		this.samples = tmpFailed;
		const tmpStatusNote = tmpStatusCount ? ` (${tmpStatusCount} with a new status)` : '';
		if (tmpFailed.length === 0)
		{
			this.forgetLocation();
			this.notify(`Moved ${tmpMovedCount} sample${tmpMovedCount === 1 ? '' : 's'} to ${tmpLocation.Name}${tmpStatusNote}.`, 'is-success');
		}
		else
		{
			this.notify(`Moved ${tmpMovedCount}${tmpStatusNote}; ${tmpFailed.length} could not be moved and are still in the list.`, 'is-danger');
		}
		this.refresh();
		this.reportMoved(tmpLocation, tmpMoved, tmpFailed, 'move-all');
	}

	/**
	 * Check Samples into Lab: set every collected sample to RECV and log "Sample received at lab." on each. The samples
	 * stay collected (their status shows the change), so they can still be moved. A sample that can't be updated is
	 * reported and left as it was.
	 * @return {Promise<void>}
	 */
	async markReceived()
	{
		if (this.markingReceived || !this.samples.length)
		{
			return;
		}
		this.markingReceived = true;
		this.refresh();
		const tmpPosition = (await Promise.resolve(this.store.getPosition()).catch(() => null)) || {};
		const tmpReceived = [];
		const tmpFailed = [];
		for (const tmpSample of this.samples.slice())
		{
			const tmpPreviousStatus = tmpSample.Status || '';
			try
			{
				await this.store.setSampleStatus(tmpSample.IDSample, RECEIVED_STATUS);
				tmpSample.Status = RECEIVED_STATUS;
				await this.store.createSampleLog(
					{
						IDSample: tmpSample.IDSample,
						CaptureLatitude: tmpPosition.CaptureLatitude || tmpSample.CaptureLatitude,
						CaptureLongitude: tmpPosition.CaptureLongitude || tmpSample.CaptureLongitude,
						FromStatus: tmpPreviousStatus,
						ToStatus: RECEIVED_STATUS,
						Notes: RECEIVED_NOTES,
					});
				tmpReceived.push(tmpSample);
			}
			catch (pError)
			{
				this.log.error(`Could not mark sample ${tmpSample.IDSample} received: ${(pError && pError.message) || pError}`);
				tmpFailed.push(tmpSample);
			}
		}
		this.markingReceived = false;
		const tmpCount = tmpReceived.length;
		if (tmpFailed.length === 0)
		{
			this.notify(`Marked ${tmpCount} sample${tmpCount === 1 ? '' : 's'} received.`, 'is-success');
		}
		else
		{
			this.notify(`Marked ${tmpCount} received; ${tmpFailed.length} could not be updated.`, 'is-danger');
		}
		this.refresh();
		if (typeof this.host.received === 'function')
		{
			try
			{
				this.host.received({ Received: tmpReceived, Failed: tmpFailed });
			}
			catch (pError)
			{
				this.log.error(`Scan Samples' host could not handle samples marked received: ${(pError && pError.message) || pError}`);
			}
		}
	}

	/**
	 * Check in and move all: Check Samples into Lab, then Move all into the cart's location, as pressing the two
	 * buttons one after the other would. Needs a location and at least one sample.
	 * @return {Promise<void>}
	 */
	async checkInAndMoveAll()
	{
		if (this.markingReceived || !this.samples.length || !this.location || !this.location.IDLocation)
		{
			return;
		}
		await this.markReceived();
		await this.moveAll();
	}

	/**
	 * Tell the host which samples were just moved, when it asked to know.
	 * @param {Object} pLocation
	 * @param {Array<Object>} pMoved
	 * @param {Array<Object>} pFailed
	 * @param {'move-all'|'scan'} pVia - Move all, or a scan saved right away
	 */
	reportMoved(pLocation, pMoved, pFailed, pVia)
	{
		if (typeof this.host.moved !== 'function')
		{
			return;
		}
		try
		{
			this.host.moved({ Location: pLocation, Moved: pMoved, Failed: pFailed, Via: pVia });
		}
		catch (pError)
		{
			this.log.error(`Scan Samples' host could not handle a move: ${(pError && pError.message) || pError}`);
		}
	}

	/**
	 * @param {number} pIDSample
	 */
	printLabel(pIDSample)
	{
		const tmpSample = this.samples.find((pSample) => pSample.IDSample === Number(pIDSample));
		if (tmpSample && typeof this.host.printLabel === 'function')
		{
			this.host.printLabel(tmpSample);
		}
	}

	/**
	 * Open or close the host's camera in the camera box, showing the box only while the camera is open. The box is
	 * shown before the camera starts, since the scanner sizes its video to it.
	 * @return {Promise<void>}
	 */
	async toggleCamera()
	{
		if (this.host.camera)
		{
			const tmpView = this.view;
			if (tmpView && !this.host.camera.isOpen())
			{
				tmpView.showCamera(true);
			}
			const tmpOpen = await this.host.camera.toggle(`${this.options.IDPrefix}-CameraBox`);
			if (tmpView)
			{
				tmpView.showCamera((typeof tmpOpen === 'boolean') ? tmpOpen : this.host.camera.isOpen());
			}
			this.refresh();
		}
	}
}

module.exports = SampleScanningProvider;
module.exports.default_configuration = _DefaultOptions;
module.exports.RECEIVED_STATUS = RECEIVED_STATUS;
module.exports.RECEIVED_NOTES = RECEIVED_NOTES;
